import { computeGainPlan, type PlanTrack } from './gain-plan.ts';

/**
 * Playback core. One AudioContext; the video element is the master
 * clock and every track is an AudioBufferSourceNode scheduled against
 * it with sample-accurate offsets.
 *
 * Graph: source -> trackGain -> sum -> masterTrim -> transport -> destination
 *
 * Two structural decisions carry the click-free guarantee:
 *
 * 1. Sources for ALL tracks run whenever the video runs, including
 *    inactive ones (into zero gain). Toggling a track is therefore
 *    purely a gain change, never a source start, so it cannot click
 *    and stays sample-aligned with the rest of the mix.
 *
 * 2. Every gain change goes through setTargetAtTime with a 0.03 s time
 *    constant: perceptually instant, physically click-free. Track gain
 *    and master trim ramp on the same clock, so the sum never bumps.
 */

export interface EngineTrack extends PlanTrack {
  label: string;
  url: string;
}

/** 0.03 s time constant settles within about 100 ms. */
const RAMP_TIME_CONSTANT = 0.03;
/** Faster constant for transport fades around stop/seek. */
const TRANSPORT_TIME_CONSTANT = 0.012;
/** Sources are stopped only after the transport fade has died away. */
const STOP_DELAY_MS = 110;
/** Scheduling lead so starts land on a clean context timestamp. */
const SCHEDULE_AHEAD_SECONDS = 0.04;
/** Drift beyond this is corrected by re-anchoring the sources. */
const MAX_DRIFT_SECONDS = 0.05;

interface TrackNode {
  track: EngineTrack;
  buffer: AudioBuffer;
  gain: GainNode;
  source: AudioBufferSourceNode | null;
}

export class AudioEngine {
  private readonly context: AudioContext;
  private readonly masterNode: GainNode;
  private readonly transportNode: GainNode;
  private readonly sumNode: GainNode;
  private readonly nodes = new Map<string, TrackNode>();
  private video: HTMLVideoElement;
  private readonly targetLufs: number;
  private readonly videoHandlers: Record<string, () => void>;

  private activeIds = new Set<string>();
  private preSoloIds: Set<string> | null = null;

  /**
   * Anchor mapping context time to media time while sources run;
   * null when stopped. Drift is measured against this.
   */
  private anchor: { contextTime: number; mediaTime: number } | null = null;
  /**
   * Every stop/start bumps the generation; late async callbacks from a
   * superseded generation must do nothing. This is what makes rapid
   * seek and pause sequences race-free.
   */
  private generation = 0;
  private driftTimer: number | null = null;

  constructor(video: HTMLVideoElement, tracks: EngineTrack[], targetLufs: number) {
    this.video = video;
    this.targetLufs = targetLufs;
    this.context = new AudioContext({ latencyHint: 'interactive' });

    this.sumNode = this.context.createGain();
    this.masterNode = this.context.createGain();
    this.transportNode = this.context.createGain();
    this.sumNode.connect(this.masterNode);
    this.masterNode.connect(this.transportNode);
    this.transportNode.connect(this.context.destination);
    this.transportNode.gain.value = 0;

    for (const track of tracks) {
      const gain = this.context.createGain();
      gain.gain.value = 0;
      gain.connect(this.sumNode);
      this.nodes.set(track.id, { track, buffer: null as never, gain, source: null });
    }

    this.videoHandlers = {
      play: () => {
        void this.context.resume();
        this.startSources();
      },
      pause: () => this.fadeOutAndStop(),
      seeking: () => this.fadeOutAndStop(),
      seeked: () => {
        if (!this.video.paused && !this.video.seeking) {
          this.startSources();
        }
      },
      waiting: () => this.fadeOutAndStop(),
      playing: () => {
        void this.context.resume();
        this.startSources();
      },
      ended: () => this.fadeOutAndStop(),
    };
    this.bindVideo();
  }

  /**
   * Hand the master clock to another, already position-matched video
   * element. Sources keep running untouched: the anchor maps context
   * time to media time and both elements agree on media time within
   * the drift threshold, so the mix passes through a picture switch
   * without so much as a ramp. The drift watcher then follows the new
   * clock and corrects if the match was imperfect.
   */
  setClockSource(video: HTMLVideoElement): void {
    if (video === this.video) {
      return;
    }
    this.unbindVideo();
    this.video = video;
    this.bindVideo();

    if (this.anchor && !video.paused) {
      const elapsed = this.context.currentTime - this.anchor.contextTime;
      const engineTime = this.anchor.mediaTime + elapsed * video.playbackRate;
      const delta = video.currentTime - engineTime;
      if (Math.abs(delta) <= 0.06) {
        // Frame-aligned pictures: absorb the residual into the anchor
        // so the mix passes through untouched. The bound keeps any
        // audio offset inside the EBU R37 lip-sync window; the preroll
        // normally lands far tighter than this.
        this.anchor = {
          contextTime: this.anchor.contextTime,
          mediaTime: this.anchor.mediaTime + delta,
        };
      } else {
        this.rampedRestart();
      }
    }
  }

  /** Notified as each track finishes decoding; drives chip readiness. */
  onTrackReady: ((id: string) => void) | null = null;

  /**
   * Notified once the background chain has been through every track,
   * successfully or not. The local media cache waits for this before
   * touching audio, so the two never pull the same file at once.
   */
  onLoadComplete: (() => void) | null = null;

  /**
   * Fetch and decode the priority track, then stream the rest one at a
   * time in the background. Playback is offered after one download
   * instead of five: on a thin link that is the difference between
   * seconds and minutes. Late arrivals join the running mix in flight,
   * sample-locked through the anchor.
   */
  async load(priorityId?: string): Promise<void> {
    const nodes = [...this.nodes.values()];
    const priority = nodes.find((node) => node.track.id === priorityId) ?? nodes[0];
    if (priority) {
      await this.loadTrack(priority);
    }
    // Sequential on purpose: the remaining tracks share the link with
    // the video buffers, so they trickle instead of competing.
    void (async () => {
      for (const node of nodes) {
        if (node === priority) {
          continue;
        }
        try {
          await this.loadTrack(node);
        } catch {
          // A failed background track simply never reports ready; the
          // priority track has already proven the route, so the miss
          // is transient and survives a reload.
        }
      }
      this.onLoadComplete?.();
    })();
  }

  private async loadTrack(node: TrackNode): Promise<void> {
    const response = await fetch(node.track.url, { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error(`failed to load track ${node.track.id}: HTTP ${response.status}`);
    }
    const encoded = await response.arrayBuffer();
    node.buffer = await this.context.decodeAudioData(encoded);
    this.attachLateSource(node);
    this.onTrackReady?.(node.track.id);
  }

  /**
   * A track that finishes decoding while the mix is already running
   * joins it in flight: the anchor dictates the exact media position
   * for the shared start timestamp, so the new source lands on the
   * same sample grid as the ones already playing. Its gain sits
   * wherever the plan put it, so nothing audible happens until the
   * viewer enables the track.
   */
  private attachLateSource(node: TrackNode): void {
    if (!this.anchor || node.source || this.video.paused) {
      return;
    }
    const startAt = this.context.currentTime + SCHEDULE_AHEAD_SECONDS;
    const mediaTime =
      this.anchor.mediaTime + (startAt - this.anchor.contextTime) * this.video.playbackRate;
    if (mediaTime >= node.buffer.duration) {
      return;
    }
    const source = this.context.createBufferSource();
    source.buffer = node.buffer;
    source.connect(node.gain);
    source.start(startAt, mediaTime);
    node.source = source;
  }

  get active(): ReadonlySet<string> {
    return this.activeIds;
  }

  get soloed(): boolean {
    return this.preSoloIds !== null;
  }

  setActive(id: string, on: boolean): void {
    // Unready tracks refuse activation, so the keyboard agrees with
    // the dimmed chips: nothing can be armed that cannot yet sound.
    if (!this.nodes.get(id)?.buffer) {
      return;
    }
    // Manual changes end a solo state; the solo memory would otherwise
    // restore a set the viewer no longer expects.
    this.preSoloIds = null;
    if (on) {
      this.activeIds.add(id);
    } else {
      this.activeIds.delete(id);
    }
    this.applyGains();
  }

  toggle(id: string): void {
    this.setActive(id, !this.activeIds.has(id));
  }

  /** Long-press solo: isolate one track, second solo restores. */
  solo(id: string): void {
    if (!this.nodes.get(id)?.buffer) {
      return;
    }
    if (this.preSoloIds && this.activeIds.size === 1 && this.activeIds.has(id)) {
      this.activeIds = this.preSoloIds;
      this.preSoloIds = null;
    } else {
      if (!this.preSoloIds) {
        this.preSoloIds = new Set(this.activeIds);
      }
      this.activeIds = new Set([id]);
    }
    this.applyGains();
  }

  setInitialActive(ids: Iterable<string>): void {
    this.activeIds = new Set([...ids].filter((id) => this.nodes.has(id)));
    this.applyGains(true);
  }

  async dispose(): Promise<void> {
    this.generation += 1;
    this.stopDriftWatch();
    this.stopSources();
    await this.context.close();
  }

  /**
   * Track gains and master trim move together on the context clock, so
   * enabling a track ramps in exactly while the combination trim ramps
   * down: the sum passes through without a bump.
   */
  private applyGains(immediate = false): void {
    const tracks = [...this.nodes.values()].map((node) => node.track);
    const plan = computeGainPlan(tracks, this.activeIds, this.targetLufs);
    const now = this.context.currentTime;
    for (const [id, node] of this.nodes) {
      const target = plan.trackGains.get(id) ?? 0;
      if (immediate) {
        node.gain.gain.value = target;
      } else {
        node.gain.gain.setTargetAtTime(target, now, RAMP_TIME_CONSTANT);
      }
    }
    if (immediate) {
      this.masterNode.gain.value = plan.masterGain;
    } else {
      this.masterNode.gain.setTargetAtTime(plan.masterGain, now, RAMP_TIME_CONSTANT);
    }
  }

  private bindVideo(): void {
    for (const [event, handler] of Object.entries(this.videoHandlers)) {
      this.video.addEventListener(event, handler);
    }
  }

  private unbindVideo(): void {
    for (const [event, handler] of Object.entries(this.videoHandlers)) {
      this.video.removeEventListener(event, handler);
    }
  }

  /**
   * (Re)start every source at the video's current position. Offsets
   * are computed against a context timestamp a fixed lead ahead, so
   * all sixteen sources begin on the same sample.
   */
  private startSources(): void {
    this.generation += 1;
    const generation = this.generation;
    this.stopSourcesNow();

    const startAt = this.context.currentTime + SCHEDULE_AHEAD_SECONDS;
    const mediaTime = this.video.currentTime + SCHEDULE_AHEAD_SECONDS * this.video.playbackRate;

    for (const node of this.nodes.values()) {
      if (!node.buffer || mediaTime >= node.buffer.duration) {
        continue;
      }
      const source = this.context.createBufferSource();
      source.buffer = node.buffer;
      source.connect(node.gain);
      source.start(startAt, mediaTime);
      node.source = source;
    }
    this.anchor = { contextTime: startAt, mediaTime };

    const now = this.context.currentTime;
    this.transportNode.gain.setTargetAtTime(1, now, RAMP_TIME_CONSTANT);
    this.watchDrift(generation);
  }

  private fadeOutAndStop(): void {
    this.generation += 1;
    const generation = this.generation;
    this.stopDriftWatch();
    this.anchor = null;
    this.transportNode.gain.setTargetAtTime(0, this.context.currentTime, TRANSPORT_TIME_CONSTANT);
    // The sources keep running into silence until the fade has settled;
    // stopping them immediately would truncate the ramp and click.
    window.setTimeout(() => {
      if (this.generation === generation) {
        this.stopSourcesNow();
      }
    }, STOP_DELAY_MS);
  }

  private stopSources(): void {
    this.stopDriftWatch();
    this.stopSourcesNow();
  }

  private stopSourcesNow(): void {
    for (const node of this.nodes.values()) {
      if (node.source) {
        try {
          node.source.stop();
        } catch {
          // Already stopped or never started; either way it is gone.
        }
        node.source.disconnect();
        node.source = null;
      }
    }
  }

  /**
   * The video element is the clock; the audio graph follows. Small
   * drift is inaudible, so correction happens only past the threshold,
   * by re-anchoring rather than by resampling: deterministic and
   * sample-accurate at the cost of a short crossfade.
   */
  private watchDrift(generation: number): void {
    this.stopDriftWatch();
    this.driftTimer = window.setInterval(() => {
      if (this.generation !== generation || !this.anchor || this.video.paused) {
        return;
      }
      const elapsed = this.context.currentTime - this.anchor.contextTime;
      if (elapsed < 0) {
        return;
      }
      const engineTime = this.anchor.mediaTime + elapsed * this.video.playbackRate;
      const drift = Math.abs(this.video.currentTime - engineTime);
      if (drift > MAX_DRIFT_SECONDS) {
        this.rampedRestart();
      }
    }, 500);
  }

  /**
   * Re-anchor with a transport fade around the source restart, so a
   * genuine correction costs a short dip instead of a click. Used only
   * when continuity cannot be preserved honestly.
   */
  private rampedRestart(): void {
    this.generation += 1;
    const generation = this.generation;
    this.transportNode.gain.setTargetAtTime(0, this.context.currentTime, TRANSPORT_TIME_CONSTANT);
    window.setTimeout(() => {
      if (this.generation === generation && !this.video.paused) {
        this.startSources();
      }
    }, STOP_DELAY_MS);
  }

  private stopDriftWatch(): void {
    if (this.driftTimer !== null) {
      window.clearInterval(this.driftTimer);
      this.driftTimer = null;
    }
  }
}
